import {MessageSquareQuote, Newspaper, Sparkles} from 'lucide-react';
import type {FeedItem} from 'iq-companies/types';
import {getFeedItemKey, groupByDay} from 'iq-companies/utils';
import {FeedCard} from './feed_card';
import {RecencyFeedCard} from './recency_feed_card';
import {QuestionFeedCard} from './question_feed_card';

// Icon per item type — matches the dashboard feed's TimelineIcon switch
// (MessageSquareQuote/Newspaper/Sparkles for experience/question/recency).
function TimelineIcon({type}: {type: FeedItem['type']}) {
  const Icon = type === 'experience' ? MessageSquareQuote : type === 'question' ? Newspaper : Sparkles;
  return <Icon className='h-4 w-4' />;
}

function FeedItemCard({item}: {item: FeedItem}) {
  if (item.type === 'recency') return <RecencyFeedCard item={item} />;
  if (item.type === 'question') return <QuestionFeedCard item={item} />;
  return <FeedCard item={item} />;
}

// Day-grouped feed list with a vertical timeline — ported measurement-for-measurement from
// the logged-in dashboard feed's design
// (frontend/src/components/dashboard/home/feed_section.module.css: .timeline/.dayLabel/
// .timelineIcon — spine at 17px from the left edge, 30px icon circles, 10px day-group dots),
// reimplemented in Tailwind since frontend-monorepo and the legacy frontend share no
// components. Colors use this app's established gray-* scale (not the dashboard's
// --iq-stroke/--iq-grey-medium tokens) to stay consistent with the rest of /companies. The
// spine/icons stay anchored at the same position regardless of the left padding below (icon
// offset = padding + 8px) — bumping the padding alone widens the gap between the timeline and
// the cards without moving the timeline itself.
export function FeedSection({items}: {items: FeedItem[]}) {
  const groups = groupByDay(items);

  if (groups.length === 0) {
    return (
      <p className='py-12 text-center text-sm text-gray-500'>
        No experiences match these filters yet.
      </p>
    );
  }

  return (
    <div className='relative pl-12'>
      <div className='absolute bottom-0 left-[17px] top-[9px] w-0.5 rounded bg-gray-200' />
      <div className='flex flex-col gap-9'>
        {groups.map((group) => (
          <div key={group.date}>
            <div className='relative -ml-12 mb-4 pl-12 text-xs font-semibold uppercase tracking-[0.08em] text-gray-400'>
              <span className='absolute left-[13px] top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-gray-200' />
              {group.label}
            </div>
            <div className='flex flex-col gap-4'>
              {group.items.map((item) => (
                <div key={getFeedItemKey(item)} className='relative'>
                  <span className='absolute -left-[45px] top-1/2 flex h-[30px] w-[30px] -translate-y-1/2 items-center justify-center rounded-full border-[1.5px] border-gray-200 bg-white text-gray-400'>
                    <TimelineIcon type={item.type} />
                  </span>
                  <FeedItemCard item={item} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
